/**
 * BinDashboardPage - Main bin status dashboard
 *
 * Follows Frontend UI Standards (CatcherPage pattern):
 * - Container with animate-enter
 * - Page header with icon box pattern
 * - MetricCards with proper variants (3-column grid)
 * - ARIA live regions for accessibility
 * - Error state handling
 *
 * Features:
 * - Real-time bin status via useBinStatus (30s polling)
 * - Packer priority badge with WCAG-compliant colors
 * - Dynamic skeleton count to prevent layout shift
 * - TV Mode link for warehouse displays
 */
import { Link, useLocation as useRouterLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Archive, Package, AlertTriangle, Maximize2, Settings2, LayoutPanelTop } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { useRole } from '@/hooks/use-role';
import { skipToken } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MetricCard } from '@/features/production-orders/components/metrics/MetricCard';
import { trpc } from '@/lib/trpc';
import { useBinStatus } from '../hooks/useBinStatus';
import { useBinOccupancy } from '../hooks/useBinOccupancy';
import { PackerPriorityBadge } from './PackerPriorityBadge';
import { ExpressBadge } from './ExpressBadge';
import { BinGrid, BinGridSkeleton } from './BinGrid';

export function BinDashboardPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const { isStationUser, isAdmin, isWarehouseCoordinator } = useRole();
  const { pathname } = useRouterLocation();
  const isTVMode = pathname.includes('/bins/tv');
  // BLU-197: bench management is coordinator-only. Fetch benches only for those
  // roles (getBenches is coordinatorProcedure) and only surface the "Benches"
  // link once at least one bench exists — keeps the feature dark by default.
  const canManageBenches = isAdmin() || isWarehouseCoordinator();
  const benchesQuery = trpc.bins.getBenches.useQuery(
    canManageBenches && selectedLocation?.id ? { locationId: selectedLocation.id } : skipToken,
    { staleTime: 60_000 },
  );
  const hasBenches = (benchesQuery.data?.length ?? 0) > 0;
  // BLU-197: bench routing flag drives discoverability — show the manage link the
  // moment routing is enabled (even before any bench exists) so a coordinator can
  // reach /benches/manage and create the FIRST bench (breaks the chicken-and-egg).
  const benchRoutingQuery = trpc.productionSettings.getBenchRoutingSettings.useQuery(undefined, {
    enabled: canManageBenches,
    staleTime: 60_000,
  });
  const benchRoutingEnabled = benchRoutingQuery.data?.benchRoutingEnabled ?? false;

  // BLU-197: live bench tiles for the Work Benches dashboard section. Same rich
  // row shape as bins (label, status, order, time-in-bin, stuck) via kind='BENCH'.
  // Coordinator/admin-only, mirroring the bench-management query.
  const benchTilesQuery = trpc.bins.getLocationBins.useQuery(
    canManageBenches && selectedLocation?.id
      ? { locationId: selectedLocation.id, kind: 'BENCH' as const }
      : skipToken,
    {
      staleTime: 30_000,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
    },
  );
  const benchTiles = benchTilesQuery.data ?? [];
  const { data: bins, isLoading: binsLoading, error: binsError } = useBinStatus();
  const { data: occupancy, isLoading: occupancyLoading } = useBinOccupancy();

  // OOS bins — always fetched (users need to see when OOS orders turn green)
  const oosBinsQuery = trpc.bins.getLocationBins.useQuery(
    selectedLocation?.id
      ? { locationId: selectedLocation.id, binType: 'OOS' as const }
      : skipToken,
    {
      staleTime: 30_000,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
    }
  );

  // Dynamic skeleton count - use cached bin count for smooth loading UX
  // TanStack Query preserves previous data during refetch, so bins?.length
  // is available even when binsLoading is true (during polling refresh)
  const showSkeleton = binsLoading && !bins;  // Only true on initial load
  const skeletonCount = bins?.length ?? 20;   // Use cached count, fallback 20

  // Calculate derived values
  const stuckCount = bins?.filter(b => b.isStuck).length ?? 0;
  const readyCount = bins?.filter(b => b.status === 'READY_TO_PACK').length ?? 0;

  // Location guard
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Archive className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{t('bins.selectLocation')}</p>
            <p className="text-muted-foreground">{t('bins.selectLocationDesc')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (binsError) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl">
        <Card className="border-destructive">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-lg font-medium">{t('bins.failedToLoad')}</p>
            <p className="text-muted-foreground">{binsError.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
    {/* ARIA live region — outside space-y container so it can't create gaps */}
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {(binsLoading || occupancyLoading) && t('bins.loadingBinStatus')}
      {!binsLoading && bins && t('bins.loadedBins', { count: bins.length })}
    </div>
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
      {/* Page header with icon box — hidden in TV mode */}
      {!isTVMode && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Archive className="h-6 w-6 text-primary" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">{t('bins.title')}</h1>
              <p className="text-muted-foreground">{t('bins.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/production/bins/manage">
                <Settings2 className="h-4 w-4 mr-2" aria-hidden="true" />
                {t('bins.manage')}
              </Link>
            </Button>
            {/* BLU-197: shown to coordinators once benches exist OR bench routing is
                enabled — the latter lets them reach the manager to create the first
                bench (chicken-and-egg fix). Dark by default when both are off. */}
            {canManageBenches && (hasBenches || benchRoutingEnabled) && (
              <Button variant="outline" asChild>
                <Link to="/production/benches/manage">
                  <LayoutPanelTop className="h-4 w-4 mr-2" aria-hidden="true" />
                  {t('bench.navLink')}
                </Link>
              </Button>
            )}
            {!isStationUser() && (
              <Button variant="outline" asChild>
                <Link to="/production/bins/tv">
                  <Maximize2 className="h-4 w-4 mr-2" aria-hidden="true" />
                  {t('bins.tvMode')}
                </Link>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Priority badge (if enabled and loaded) */}
      {occupancy?.packerPriority && (
        <PackerPriorityBadge
          priority={occupancy.packerPriority}
          occupancyPercent={occupancy.occupancyPercent}
        />
      )}

      {/* Next eligible bin, Express then assignment age (if any) - shown above grid for visibility */}
      {occupancy?.oldestReadyBin && (
        <Card className="border-green-200 dark:border-green-800">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t('bins.nextBinToPack')}</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {occupancy.oldestReadyBin.label}
            </p>
            {occupancy.oldestReadyBin.isExpress && <ExpressBadge className="mt-1" />}
          </CardContent>
        </Card>
      )}

      {/* Bin grids by size */}
      {(() => {
        const allBins = bins ?? [];
        const standardBins = allBins.filter(b => !b.binSize || b.binSize === 'STANDARD');
        const largeBins = allBins.filter(b => b.binSize === 'LARGE');
        const hugeBins = allBins.filter(b => b.binSize === 'HUGE');

        return (
          <>
            {/* Standard bins — always shown */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t('bins.standardBins')}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {showSkeleton ? (
                  <BinGridSkeleton count={skeletonCount} />
                ) : (
                  <BinGrid bins={standardBins} binSize="STANDARD" />
                )}
              </CardContent>
            </Card>

            {/* Large bins — shown if any exist */}
            {largeBins.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{t('bins.largeBins')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <BinGrid bins={largeBins} binSize="LARGE" />
                </CardContent>
              </Card>
            )}

            {/* Huge bins — shown if any exist */}
            {hugeBins.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{t('bins.hugeBins')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <BinGrid bins={hugeBins} binSize="HUGE" />
                </CardContent>
              </Card>
            )}
          </>
        );
      })()}

      {/* Work Benches grid (BLU-197) — coordinator/admin-only, shown when any
          bench exists. Sits below Huge Bins and above OOS Bins. */}
      {benchTiles.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('bins.workBenches')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <BinGrid bins={benchTiles} binSize="BENCH" />
          </CardContent>
        </Card>
      )}

      {/* OOS bins grid — shown when any exist for this location */}
      {oosBinsQuery.data && oosBinsQuery.data.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('bins.outOfStockBins')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <BinGrid bins={oosBinsQuery.data} binSize="STANDARD" />
          </CardContent>
        </Card>
      )}

      {/* Stats row - below bin grid for less visual noise */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard
          variant="production"
          title={t('bins.total')}
          value={String(occupancy?.totalBins ?? 0)}
          icon={Archive}
        />
        <MetricCard
          variant="total"
          title={t('bins.readyToPack')}
          value={String(readyCount)}
          icon={Package}
        />
        <MetricCard
          variant={stuckCount > 0 ? 'danger' : 'gst'}
          title={t('bins.stuck24h')}
          value={String(stuckCount)}
          icon={AlertTriangle}
        />
      </div>
    </div>
    </>
  );
}
